/**
 * Profile Service — API calls for profile management (avatar, nickname, deletion).
 * Uses the centralised auth token from authService.
 */

import { getAccessToken } from './authService';

const API_BASE = '/api/profile';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface ProfileData {
  userId: string;
  nickname: string;
  avatar: string;
}

export interface ProfileError {
  code: string;
  message: string;
}

// ─── HTTP helper ────────────────────────────────────────────────────────────

async function profileFetch<T>(
  path: string,
  method: 'GET' | 'PUT' | 'DELETE',
  body?: Record<string, unknown>,
): Promise<T> {
  const token = getAccessToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE}${path}`, {
    method,
    headers,
    credentials: 'include',
    body: body ? JSON.stringify(body) : undefined,
  });

  if (!response.ok) {
    const error: ProfileError = await response.json().catch(() => ({
      code: 'UNKNOWN_ERROR',
      message: 'An unexpected error occurred',
    }));
    throw error;
  }

  // DELETE may return 202 with no body
  if (response.status === 202 || response.headers.get('content-length') === '0') {
    return {} as T;
  }

  return response.json() as Promise<T>;
}

// ─── Profile API methods ────────────────────────────────────────────────────

export async function getProfile(): Promise<ProfileData> {
  return profileFetch<ProfileData>('', 'GET');
}

export async function updateAvatar(avatar: string): Promise<{ success: boolean }> {
  return profileFetch<{ success: boolean }>('/avatar', 'PUT', { avatar });
}

export async function updateNickname(nickname: string): Promise<{ success: boolean }> {
  return profileFetch<{ success: boolean }>('/nickname', 'PUT', { nickname });
}

export async function deleteAccount(): Promise<void> {
  await profileFetch<Record<string, unknown>>('', 'DELETE');
}
